import { MouseEvent, ReactNode } from 'react';
import { surfaceShadows } from '../surfaceStyles';
import {
  Box,
  Checkbox,
  CircularProgress,
  ListItemButton,
  ListItemButtonProps,
  ListItemText,
  ListItemTextProps,
  Typography,
} from '@mui/material';
import { Item } from '../domain/item';
import { useImageViewport } from '../features/images/useImageViewport';
import ImageWithStatus from './ImageWithStatus';

export const itemListCardStyles = {
  border: 0,
  borderRadius: 1,
  boxShadow: surfaceShadows.control,
  overflow: 'hidden',
  boxSizing: 'border-box',
  alignItems: 'stretch',
  position: 'relative',
} as const;

/** Shared card presentation; callers own selection, gestures and operations. */
export function ItemListCardContent({
  item,
  title,
  metadata,
  height,
  imageWidth = height,
  textGap = 1.5,
  imageAlt = '',
  imageTestId,
  actionProps,
  primaryTypographyProps,
  secondaryTypographyProps,
  checkbox,
  trailing,
}: {
  item: Pick<Item, 'image' | 'imageLoading' | 'requestImage' | 'storagePath'>;
  title: string;
  metadata: ReactNode;
  height: number;
  imageWidth?: number;
  textGap?: number;
  imageAlt?: string;
  imageTestId?: string;
  actionProps: ListItemButtonProps;
  primaryTypographyProps?: ListItemTextProps['primaryTypographyProps'];
  secondaryTypographyProps?: ListItemTextProps['secondaryTypographyProps'];
  checkbox?: {
    checked: boolean;
    label: string;
    disabled?: boolean;
    onToggle: (event: MouseEvent) => void;
  };
  trailing?: ReactNode;
}) {
  const imageViewport = useImageViewport(item.requestImage);
  return (
    <>
      <ListItemButton
        {...actionProps}
        sx={{
          minHeight: height,
          minWidth: 0,
          alignItems: 'stretch',
          pl: 0,
          pr: checkbox ? '52px' : 0,
          py: 0,
          touchAction: 'pan-y',
        }}
      >
        <Box
          ref={imageViewport}
          data-testid={imageTestId}
          sx={{
            width: imageWidth,
            minWidth: imageWidth,
            flex: `0 0 ${imageWidth}px`,
            alignSelf: 'stretch',
            position: 'relative',
            overflow: 'hidden',
            bgcolor: 'background.default',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'grid',
              placeItems: 'center',
            }}
          >
            {item.imageLoading ? (
              <CircularProgress size={24} aria-label="Loading image" />
            ) : item.image ? (
              <ImageWithStatus
                src={item.image}
                alt={imageAlt}
                style={{
                  display: 'block',
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center center',
                  pointerEvents: 'none',
                }}
              />
            ) : (
              <Typography
                variant="caption"
                color="text.secondary"
                textAlign="center"
              >
                {item.storagePath ? 'Image unavailable' : 'No image'}
              </Typography>
            )}
          </Box>
        </Box>
        <ListItemText
          primary={title}
          secondary={metadata}
          primaryTypographyProps={primaryTypographyProps}
          secondaryTypographyProps={secondaryTypographyProps}
          sx={{
            m: 0,
            px: textGap,
            minWidth: 0,
            overflowWrap: 'anywhere',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        />
      </ListItemButton>
      {checkbox && (
        <Checkbox
          checked={checkbox.checked}
          disabled={checkbox.disabled}
          sx={{
            width: 44,
            height: 44,
            position: 'absolute',
            right: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1,
          }}
          inputProps={{ 'aria-label': checkbox.label }}
          onClick={(event) => {
            event.stopPropagation();
            checkbox.onToggle(event);
          }}
        />
      )}
      {trailing}
    </>
  );
}
